import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useStore } from '../../../../stores/Store';
import { isMobile } from 'is-mobile';

import './styles.scss';

const AdminBenefitsCreditBuilder = observer(() => {
    let navigate = useNavigate();
    const commonStore = useStore();
    const [error, setError] = useState('');
    const [fileType, setFileType] = useState('');
    const [formData, setFormData] = useState({});

    const handleFileTypeChange = (type) => {
        setFileType(type);
        setFormData({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = () => {
        if (fileType === 'monthly') {
            generateMonthlyFile(formData);
        } else if (fileType === 'daily') {
            generateDailyFile(formData);
        } else {
            setError('Please select a file type.');
        }
    };

    const getDefault = (value, length, padDirection = 'start', padChar = ' ') => {
        // Default to an empty string and apply padding
        value = value || '';
        if (padDirection === 'start') {
            return value.padStart(length, padChar);
        }
        return value.padEnd(length, padChar);
    };

    const generateMonthlyFile = (data) => {
        // Generate the monthly file content with default values and correct padding
        let header = `H${getDefault(data.supplierRefNumber, 10)}${getDefault(data.monthEndDate, 8)}${getDefault(data.versionNumber, 2)}${getDefault(data.fileCreationDate, 8)}${getDefault(data.tradingName, 60)}${' '.repeat(611)}\n`;
        let dataRecords = `D${getDefault(data.saIdNumber, 13)}${getDefault(data.nonSaIdNumber, 16, 'end')}${getDefault(data.gender, 1, 'end')}${getDefault(data.dateOfBirth, 8)}${getDefault(data.branchCode, 8, 'end')}${getDefault(data.accountNumber, 25, 'end')}${getDefault(data.subAccountNumber, 4, 'end')}${getDefault(data.surname, 25, 'end')}${getDefault(data.title, 5, 'end')}${getDefault(data.forename1, 14, 'end')}${getDefault(data.forename2, 14, 'end')}${getDefault(data.forename3, 14, 'end')}${getDefault(data.residentialAddress1, 25, 'end')}${getDefault(data.residentialAddress2, 25, 'end')}${getDefault(data.residentialAddress3, 25, 'end')}${getDefault(data.residentialAddress4, 25, 'end')}${getDefault(data.residentialPostalCode, 6, 'end')}${getDefault(data.ownerTenant, 1, 'end')}${getDefault(data.postalAddress1, 25, 'end')}${getDefault(data.postalAddress2, 25, 'end')}${getDefault(data.postalAddress3, 25, 'end')}${getDefault(data.postalAddress4, 25, 'end')}${getDefault(data.postalPostalCode, 6, 'end')}${getDefault(data.ownershipType, 2, 'end')}${getDefault(data.loanReasonCode, 2, 'end')}${getDefault(data.paymentType, 2, 'end')}${getDefault(data.typeOfAccount, 2, 'end')}${getDefault(data.dateAccountOpened, 8)}${getDefault(data.deferredPaymentDate, 8)}${getDefault(data.dateOfLastPayment, 8)}${getDefault(data.openingBalance, 9, 'start', '0')}${getDefault(data.currentBalance, 9, 'start', '0')}${getDefault(data.currentBalanceIndicator, 1, 'end')}${getDefault(data.amountOverdue, 9, 'start', '0')}${getDefault(data.instalmentAmount, 9, 'start', '0')}${getDefault(data.monthsInArrears, 2, 'start', '0')}${getDefault(data.statusCode, 2, 'end')}${getDefault(data.repaymentFrequency, 2, 'start', '0')}${getDefault(data.terms, 4, 'start', '0')}${getDefault(data.statusDate, 8)}${getDefault(data.oldSupplierBranchCode, 8, 'end')}${getDefault(data.oldAccountNumber, 25, 'end')}${getDefault(data.oldSubAccountNumber, 4, 'end')}${getDefault(data.oldSupplierReferenceNumber, 10, 'start', '0')}${getDefault(data.homeTelephone, 16, 'end')}${getDefault(data.cellularTelephone, 16, 'end')}${getDefault(data.workTelephone, 16, 'end')}${getDefault(data.employerDetail, 60, 'end')}${getDefault(data.income, 9, 'start', '0')}${getDefault(data.incomeFrequency, 1, 'end')}${getDefault(data.occupation, 20, 'end')}${getDefault(data.thirdPartyName, 60, 'end')}${getDefault(data.accountSoldToThirdParty, 2, 'start', '0')}${getDefault(data.noOfParticipantsInJointLoan, 3, 'start', '0')}${' '.repeat(2)}\n`;
        let trailer = `T${getDefault(data.numberOfRecords, 9, 'start', '0')}${' '.repeat(689)}\n`;

        let content = header + dataRecords + trailer;

        // File name using the provided naming convention
        let fileName = `SRN_ALL_T702_M_${data.monthEndDate}_01_01.txt`;
        downloadFile(content, fileName);
    };

    const generateDailyFile = (data) => {
        // Generate the daily file content with default values and correct padding
        let dataRecord = `R${getDefault(data.saIdNumber, 13)}${getDefault(data.nonSaIdNumber, 16, 'end')}${getDefault(data.gender, 1, 'end')}${getDefault(data.dateOfBirth, 8)}${getDefault(data.branchCode, 8, 'end')}${getDefault(data.accountNumber, 25, 'end')}${getDefault(data.subAccountNumber, 4, 'end')}${getDefault(data.surname, 25, 'end')}${getDefault(data.title, 5, 'end')}${getDefault(data.forename1, 14, 'end')}${getDefault(data.forename2, 14, 'end')}${getDefault(data.forename3, 14, 'end')}${getDefault(data.residentialAddress1, 25, 'end')}${getDefault(data.residentialAddress2, 25, 'end')}${getDefault(data.residentialAddress3, 25, 'end')}${getDefault(data.residentialAddress4, 25, 'end')}${getDefault(data.residentialPostalCode, 6, 'end')}${getDefault(data.ownerTenant, 1, 'end')}${getDefault(data.postalAddress1, 25, 'end')}${getDefault(data.postalAddress2, 25, 'end')}${getDefault(data.postalAddress3, 25, 'end')}${getDefault(data.postalAddress4, 25, 'end')}${getDefault(data.postalPostalCode, 6, 'end')}${getDefault(data.ownershipType, 2, 'end')}${getDefault(data.loanReasonCode, 2, 'end')}${getDefault(data.paymentType, 2, 'end')}${getDefault(data.typeOfAccount, 2, 'end')}${getDefault(data.dateAccountOpened, 8)}${getDefault(data.deferredPaymentDate, 8)}${getDefault(data.dateOfLastPayment, 8)}${getDefault(data.openingBalance, 9, 'start', '0')}${getDefault(data.currentBalance, 9, 'start', '0')}${getDefault(data.currentBalanceIndicator, 1, 'end')}${getDefault(data.amountOverdue, 9, 'start', '0')}${getDefault(data.instalmentAmount, 9, 'start', '0')}${getDefault(data.monthsInArrears, 2, 'start', '0')}${getDefault(data.statusCode, 2, 'end')}${getDefault(data.repaymentFrequency, 2, 'start', '0')}${getDefault(data.terms, 4, 'start', '0')}${getDefault(data.statusDate, 8)}${getDefault(data.oldSupplierBranchCode, 8, 'end')}${getDefault(data.oldAccountNumber, 25, 'end')}${getDefault(data.oldSubAccountNumber, 4, 'end')}${getDefault(data.oldSupplierReferenceNumber, 10, 'start', '0')}${getDefault(data.homeTelephone, 16, 'end')}${getDefault(data.cellularTelephone, 16, 'end')}${getDefault(data.workTelephone, 16, 'end')}${getDefault(data.employerDetail, 60, 'end')}${getDefault(data.income, 9, 'start', '0')}${getDefault(data.incomeFrequency, 1, 'end')}${getDefault(data.occupation, 20, 'end')}${getDefault(data.thirdPartyName, 60, 'end')}${getDefault(data.accountSoldToThirdParty, 2, 'start', '0')}${getDefault(data.noOfParticipantsInJointLoan, 3, 'start', '0')}${' '.repeat(2)}\n`;

        // File name using the provided naming convention
        let fileName = `SRN_ALL_T702_D_${data.transactionDate}_01_01.txt`;
        downloadFile(dataRecord, fileName);
    };

    const downloadFile = (content, fileName) => {
        const blob = new Blob([content], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    return (
        <div className="admin-benefits-credit-builder">
            {isMobile() ? (
                <div style={{ background: "#F4F5FA", width: '100%' }}></div>
            ) : (
                <div>
                    <header>
                        <h1>Generate Credit Builder Files</h1>
                        <div>
                            <button onClick={() => handleFileTypeChange('monthly')}>Monthly File</button>
                            <br/>
                            <button onClick={() => handleFileTypeChange('daily')}>Daily File</button>
                        </div>
                    </header>
                    {fileType && (
                        <main>
                                <form className='credit-builder-forms'>
                                {fileType === 'monthly' && (
                                    <>
                                        {/* Monthly Form Inputs */}
                                        <label>
                                            Header Indicator (H):
                                            <input type="text" name="headerIndicator" value="H" disabled />
                                        </label>
                                        <label>
                                            Supplier Reference Number:
                                            <input type="text" name="supplierRefNumber" onChange={handleInputChange} maxLength="10" />
                                        </label>
                                        <label>
                                            Month End Date (CCYYMMDD):
                                            <input type="text" name="monthEndDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Version Number:
                                            <input type="text" name="versionNumber" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            File Creation Date (CCYYMMDD):
                                            <input type="text" name="fileCreationDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Trading Name/Brand Name:
                                            <input type="text" name="tradingName" onChange={handleInputChange} maxLength="60" />
                                        </label>
                                        <label>
                                            Filler:
                                            <input type="text" name="filler" onChange={handleInputChange} maxLength="611" />
                                        </label>
                                        {/* Monthly Data Records */}
                                        <label>
                                            Data Indicator (D):
                                            <input type="text" name="dataIndicator" value="D" disabled />
                                        </label>
                                        <label>
                                            SA ID Number:
                                            <input type="text" name="saIdNumber" onChange={handleInputChange} maxLength="13" />
                                        </label>
                                        <label>
                                            Non-SA ID Number:
                                            <input type="text" name="nonSaIdNumber" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Gender:
                                            <input type="text" name="gender" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Date of Birth (CCYYMMDD):
                                            <input type="text" name="dateOfBirth" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Branch Code:
                                            <input type="text" name="branchCode" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Account Number:
                                            <input type="text" name="accountNumber" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Sub-Account Number:
                                            <input type="text" name="subAccountNumber" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Surname:
                                            <input type="text" name="surname" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Title:
                                            <input type="text" name="title" onChange={handleInputChange} maxLength="5" />
                                        </label>
                                        <label>
                                            Forename or Initial 1:
                                            <input type="text" name="forename1" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Forename or Initial 2:
                                            <input type="text" name="forename2" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Forename or Initial 3:
                                            <input type="text" name="forename3" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Residential Address Line 1:
                                            <input type="text" name="residentialAddress1" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 2:
                                            <input type="text" name="residentialAddress2" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 3:
                                            <input type="text" name="residentialAddress3" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 4:
                                            <input type="text" name="residentialAddress4" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Code of Residential Address:
                                            <input type="text" name="residentialPostalCode" onChange={handleInputChange} maxLength="6" />
                                        </label>
                                        <label>
                                            Owner/Tenant:
                                            <input type="text" name="ownerTenant" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Postal Address Line 1:
                                            <input type="text" name="postalAddress1" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 2:
                                            <input type="text" name="postalAddress2" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 3:
                                            <input type="text" name="postalAddress3" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 4:
                                            <input type="text" name="postalAddress4" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Code of Postal Address:
                                            <input type="text" name="postalPostalCode" onChange={handleInputChange} maxLength="6" />
                                        </label>
                                        <label>
                                            Ownership Type:
                                            <input type="text" name="ownershipType" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Loan Reason Code:
                                            <input type="text" name="loanReasonCode" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Payment Type:
                                            <input type="text" name="paymentType" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Type of Account:
                                            <input type="text" name="typeOfAccount" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Date Account Opened (CCYYMMDD):
                                            <input type="text" name="dateAccountOpened" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Deferred Payment Date (CCYYMMDD):
                                            <input type="text" name="deferredPaymentDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Date of Last Payment (CCYYMMDD):
                                            <input type="text" name="dateOfLastPayment" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Opening Balance/Credit Limit:
                                            <input type="text" name="openingBalance" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Current Balance:
                                            <input type="text" name="currentBalance" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Current Balance Indicator:
                                            <input type="text" name="currentBalanceIndicator" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Amount Overdue:
                                            <input type="text" name="amountOverdue" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Instalment Amount:
                                            <input type="text" name="instalmentAmount" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Months in Arrears:
                                            <input type="text" name="monthsInArrears" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Status Code:
                                            <input type="text" name="statusCode" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Repayment Frequency:
                                            <input type="text" name="repaymentFrequency" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Terms:
                                            <input type="text" name="terms" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Status Date (CCYYMMDD):
                                            <input type="text" name="statusDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Old Supplier Branch Code:
                                            <input type="text" name="oldSupplierBranchCode" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Old Account Number:
                                            <input type="text" name="oldAccountNumber" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Old Sub-Account Number:
                                            <input type="text" name="oldSubAccountNumber" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Old Supplier Reference Number:
                                            <input type="text" name="oldSupplierReferenceNumber" onChange={handleInputChange} maxLength="10" />
                                        </label>
                                        <label>
                                            Home Telephone:
                                            <input type="text" name="homeTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Cellular Telephone:
                                            <input type="text" name="cellularTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Work Telephone:
                                            <input type="text" name="workTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Employer Detail:
                                            <input type="text" name="employerDetail" onChange={handleInputChange} maxLength="60" />
                                        </label>
                                        <label>
                                            Income:
                                            <input type="text" name="income" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Income Frequency:
                                            <input type="text" name="incomeFrequency" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Occupation:
                                            <input type="text" name="occupation" onChange={handleInputChange} maxLength="20" />
                                        </label>
                                        <label>
                                            Third Party Name:
                                            <input type="text" name="thirdPartyName" onChange={handleInputChange} maxLength="60" />
                                        </label>
                                        <label>
                                            Account Sold to Third Party:
                                            <input type="text" name="accountSoldToThirdParty" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            No of Participants in Joint Loan:
                                            <input type="text" name="noOfParticipantsInJointLoan" onChange={handleInputChange} maxLength="3" />
                                        </label>
                                        <label>
                                            Filler:
                                            <input type="text" name="filler" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                    </>
                                )}
                                {fileType === 'daily' && (
                                    <>
                                        {/* Daily Form Inputs */}
                                        <label>
                                            Data Indicator (R):
                                            <input type="text" name="dataIndicator" value="R" disabled />
                                        </label>
                                        <label>
                                            SA ID Number:
                                            <input type="text" name="saIdNumber" onChange={handleInputChange} maxLength="13" />
                                        </label>
                                        <label>
                                            Non-SA ID Number:
                                            <input type="text" name="nonSaIdNumber" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Gender:
                                            <input type="text" name="gender" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Date of Birth (CCYYMMDD):
                                            <input type="text" name="dateOfBirth" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Branch Code:
                                            <input type="text" name="branchCode" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Account Number:
                                            <input type="text" name="accountNumber" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Sub-Account Number:
                                            <input type="text" name="subAccountNumber" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Surname:
                                            <input type="text" name="surname" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Title:
                                            <input type="text" name="title" onChange={handleInputChange} maxLength="5" />
                                        </label>
                                        <label>
                                            Forename or Initial 1:
                                            <input type="text" name="forename1" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Forename or Initial 2:
                                            <input type="text" name="forename2" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Forename or Initial 3:
                                            <input type="text" name="forename3" onChange={handleInputChange} maxLength="14" />
                                        </label>
                                        <label>
                                            Residential Address Line 1:
                                            <input type="text" name="residentialAddress1" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 2:
                                            <input type="text" name="residentialAddress2" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 3:
                                            <input type="text" name="residentialAddress3" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Residential Address Line 4:
                                            <input type="text" name="residentialAddress4" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Code of Residential Address:
                                            <input type="text" name="residentialPostalCode" onChange={handleInputChange} maxLength="6" />
                                        </label>
                                        <label>
                                            Owner/Tenant:
                                            <input type="text" name="ownerTenant" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Postal Address Line 1:
                                            <input type="text" name="postalAddress1" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 2:
                                            <input type="text" name="postalAddress2" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 3:
                                            <input type="text" name="postalAddress3" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Address Line 4:
                                            <input type="text" name="postalAddress4" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Postal Code of Postal Address:
                                            <input type="text" name="postalPostalCode" onChange={handleInputChange} maxLength="6" />
                                        </label>
                                        <label>
                                            Ownership Type:
                                            <input type="text" name="ownershipType" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Loan Reason Code:
                                            <input type="text" name="loanReasonCode" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Payment Type:
                                            <input type="text" name="paymentType" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Type of Account:
                                            <input type="text" name="typeOfAccount" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Date Account Opened (CCYYMMDD):
                                            <input type="text" name="dateAccountOpened" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Deferred Payment Date (CCYYMMDD):
                                            <input type="text" name="deferredPaymentDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Date of Last Payment (CCYYMMDD):
                                            <input type="text" name="dateOfLastPayment" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Opening Balance/Credit Limit:
                                            <input type="text" name="openingBalance" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Current Balance:
                                            <input type="text" name="currentBalance" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Current Balance Indicator:
                                            <input type="text" name="currentBalanceIndicator" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Amount Overdue:
                                            <input type="text" name="amountOverdue" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Instalment Amount:
                                            <input type="text" name="instalmentAmount" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Months in Arrears:
                                            <input type="text" name="monthsInArrears" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Status Code:
                                            <input type="text" name="statusCode" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Repayment Frequency:
                                            <input type="text" name="repaymentFrequency" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            Terms:
                                            <input type="text" name="terms" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Status Date (CCYYMMDD):
                                            <input type="text" name="statusDate" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Old Supplier Branch Code:
                                            <input type="text" name="oldSupplierBranchCode" onChange={handleInputChange} maxLength="8" />
                                        </label>
                                        <label>
                                            Old Account Number:
                                            <input type="text" name="oldAccountNumber" onChange={handleInputChange} maxLength="25" />
                                        </label>
                                        <label>
                                            Old Sub-Account Number:
                                            <input type="text" name="oldSubAccountNumber" onChange={handleInputChange} maxLength="4" />
                                        </label>
                                        <label>
                                            Old Supplier Reference Number:
                                            <input type="text" name="oldSupplierReferenceNumber" onChange={handleInputChange} maxLength="10" />
                                        </label>
                                        <label>
                                            Home Telephone:
                                            <input type="text" name="homeTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Cellular Telephone:
                                            <input type="text" name="cellularTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Work Telephone:
                                            <input type="text" name="workTelephone" onChange={handleInputChange} maxLength="16" />
                                        </label>
                                        <label>
                                            Employer Detail:
                                            <input type="text" name="employerDetail" onChange={handleInputChange} maxLength="60" />
                                        </label>
                                        <label>
                                            Income:
                                            <input type="text" name="income" onChange={handleInputChange} maxLength="9" />
                                        </label>
                                        <label>
                                            Income Frequency:
                                            <input type="text" name="incomeFrequency" onChange={handleInputChange} maxLength="1" />
                                        </label>
                                        <label>
                                            Occupation:
                                            <input type="text" name="occupation" onChange={handleInputChange} maxLength="20" />
                                        </label>
                                        <label>
                                            Third Party Name:
                                            <input type="text" name="thirdPartyName" onChange={handleInputChange} maxLength="60" />
                                        </label>
                                        <label>
                                            Account Sold to Third Party:
                                            <input type="text" name="accountSoldToThirdParty" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                        <label>
                                            No of Participants in Joint Loan:
                                            <input type="text" name="noOfParticipantsInJointLoan" onChange={handleInputChange} maxLength="3" />
                                        </label>
                                        <label>
                                            Filler:
                                            <input type="text" name="filler" onChange={handleInputChange} maxLength="2" />
                                        </label>
                                    </>
                                )}
                                <button type="button" onClick={handleSubmit}>
                                    Generate {fileType.charAt(0).toUpperCase() + fileType.slice(1)} File
                                </button>
                            </form>
                        </main>
                    )}
                    {error && <div className="error">{error}</div>}
                </div>
            )}
        </div>
    );
});

export default AdminBenefitsCreditBuilder;
